/**
 * 마크다운 파서
 * 마크다운 문서를 파싱하여 구조화된 정보 추출
 */

import type { DocumentHeading, CodeBlockInfo } from '../../types/contentIndex'

export interface ParsedMarkdown {
  title: string | null
  tags: string[]
  links: string[]
  headings: DocumentHeading[]
  codeBlocks: CodeBlockInfo[]
  images: string[]
}

/**
 * 마크다운 문서 파싱
 */
export function parseMarkdown(content: string): ParsedMarkdown {
  const lines = content.split('\n')
  
  const result: ParsedMarkdown = {
    title: null,
    tags: [],
    links: [],
    headings: [],
    codeBlocks: [],
    images: []
  }
  
  let inCodeBlock = false
  let codeBlockStart = 0
  let codeBlockLanguage = ''
  let inFrontmatter = false
  let frontmatterContent = ''
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    
    // Frontmatter 처리
    if (i === 0 && line === '---') {
      inFrontmatter = true
      continue
    }
    
    if (inFrontmatter) {
      if (line === '---') {
        inFrontmatter = false
        parseFrontmatter(frontmatterContent, result)
        continue
      }
      frontmatterContent += line + '\n'
      continue
    }
    
    // 코드 블록 처리
    if (line.startsWith('```')) {
      if (!inCodeBlock) {
        inCodeBlock = true
        codeBlockStart = i
        codeBlockLanguage = line.slice(3).trim()
      } else {
        inCodeBlock = false
        result.codeBlocks.push({
          language: codeBlockLanguage,
          startLine: codeBlockStart + 1,
          endLine: i + 1,
          lineCount: i - codeBlockStart - 1
        })
      }
      continue
    }
    
    // 코드 블록 내부는 파싱하지 않음
    if (inCodeBlock) continue
    
    // 헤딩 추출
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (headingMatch) {
      const level = headingMatch[1].length
      const text = headingMatch[2].trim()
      
      // 첫 번째 헤딩을 제목으로 사용
      if (result.title === null && level === 1) {
        result.title = text
      }
      
      result.headings.push({
        level,
        text,
        line: i + 1,
        id: generateHeadingId(text)
      })
    }
    
    // 링크 추출
    const linkMatches = line.matchAll(/\[([^\]]+)\]\(([^)]+)\)/g)
    for (const match of linkMatches) {
      const url = match[2]
      // 내부 링크인 경우 (상대 경로 또는 #으로 시작)
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        result.links.push(url)
      }
    }
    
    // 이미지 추출
    const imageMatches = line.matchAll(/!\[([^\]]*)\]\(([^)]+)\)/g)
    for (const match of imageMatches) {
      result.images.push(match[2])
    }
    
    // 태그 추출 (해시태그 형식)
    const tagMatches = line.matchAll(/#([^\s#]+)/g)
    for (const match of tagMatches) {
      const tag = match[1]
      // 헤딩 마커가 아닌 경우만
      if (!line.trim().startsWith('#')) {
        result.tags.push(tag)
      }
    }
  }
  
  // 중복 제거
  result.tags = [...new Set(result.tags)]
  result.links = [...new Set(result.links)]
  result.images = [...new Set(result.images)]
  
  return result
}

/**
 * Frontmatter 파싱
 */
function parseFrontmatter(content: string, result: ParsedMarkdown): void {
  const lines = content.split('\n')
  
  for (const line of lines) {
    // title 추출
    if (line.startsWith('title:')) {
      result.title = line.slice(6).trim().replace(/^["']|["']$/g, '')
    }
    
    // tags 추출
    if (line.startsWith('tags:')) {
      const tagLine = line.slice(5).trim()
      // 배열 형식: tags: [tag1, tag2]
      if (tagLine.startsWith('[')) {
        const tags = tagLine.slice(1, -1).split(',').map(t => t.trim().replace(/^["']|["']$/g, ''))
        result.tags.push(...tags)
      } else {
        // 단일 태그
        result.tags.push(tagLine.replace(/^["']|["']$/g, ''))
      }
    }
    
    // YAML 배열 형식
    if (line.trim().startsWith('- ') && lines[lines.indexOf(line) - 1]?.startsWith('tags:')) {
      const tag = line.trim().slice(2).replace(/^["']|["']$/g, '')
      result.tags.push(tag)
    }
  }
}

/**
 * 헤딩 ID 생성 (앵커용)
 */
function generateHeadingId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s가-힣-]/g, '') // 특수문자 제거
    .replace(/\s+/g, '-')           // 공백을 하이픈으로
    .replace(/-+/g, '-')            // 연속 하이픈 정리
    .replace(/^-|-$/g, '')          // 앞뒤 하이픈 제거
}

/**
 * 마크다운에서 텍스트만 추출
 */
export function extractPlainText(markdown: string): string {
  let text = markdown
  
  // 코드 블록 제거
  text = text.replace(/```[\s\S]*?```/g, '')
  text = text.replace(/`[^`]*`/g, '')
  
  // 링크 텍스트만 유지
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  
  // 이미지 제거
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
  
  // 마크다운 문법 제거
  text = text.replace(/^#{1,6}\s+/gm, '')
  text = text.replace(/^[\*\-\+]\s+/gm, '')
  text = text.replace(/^\d+\.\s+/gm, '')
  text = text.replace(/^>\s+/gm, '')
  text = text.replace(/[\*_]{1,3}([^\*_]+)[\*_]{1,3}/g, '$1')
  
  // HTML 태그 제거
  text = text.replace(/<[^>]+>/g, '')
  
  // 연속 공백 정리
  text = text.replace(/\s+/g, ' ')
  
  return text.trim()
}

/**
 * 문서 미리보기 생성
 */
export function generatePreview(content: string, maxLength: number = 200): string {
  const plainText = extractPlainText(content)
  
  if (plainText.length <= maxLength) {
    return plainText
  }
  
  // 단어 단위로 자르기
  const words = plainText.split(/\s+/)
  let preview = ''
  
  for (const word of words) {
    if ((preview + word).length > maxLength) {
      break
    }
    preview += (preview ? ' ' : '') + word
  }
  
  return preview + '...'
}

/**
 * 목차 생성
 */
export function generateTableOfContents(headings: DocumentHeading[]): string {
  let toc = ''
  
  for (const heading of headings) {
    const indent = '  '.repeat(heading.level - 1)
    toc += `${indent}- [${heading.text}](#${heading.id})\n`
  }
  
  return toc
}